<script setup lang="ts">
import {ref, watch} from "vue";
import {Loader2, Smartphone, TriangleAlert} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Dialog} from "@/core/components/ui/dialog";
import PreCertificationDialogContent
    from "@/customer-certification/components/pre-certification-dialog/PreCertificationDialogContent.vue";
import AgendaPatientSearch, {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import {customerApi} from "@/container";
import {Customer} from "@/customer/interfaces/Customer";

/**
 * Who the journey is for.
 *
 * The file is read in full behind the pick, for two things the search result
 * does not carry on its own: the consumer key the program endpoints take, and
 * the mobile number the recap is texted to. A patient without one is offered
 * the counter's existing pre-certification window rather than being planned
 * for and left unreachable.
 */
const patient = defineModel<SelectedPatient | null>('patient', {required: true});
const customer = defineModel<Customer | null>('customer', {required: true});

const loading = ref(false);
const failed = ref(false);
const certificationOpen = ref(false);

/**
 * Identity of the file being read, so a slow answer for a patient the
 * pharmacist has already moved on from never lands on the screen.
 */
let latestId: number | null = null;

const load = async (id: number) => {
    latestId = id;
    loading.value = true;
    failed.value = false;

    try {
        const loaded = await customerApi().read(id);

        if (latestId !== id) {
            return;
        }

        customer.value = loaded;
        patient.value = {
            id: loaded.id,
            name: loaded.information?.name ?? patient.value?.name ?? '',
            phone: loaded.information?.phone ?? null,
            consumerKey: loaded.consumer_key ?? null
        };
    } catch {
        if (latestId !== id) {
            return;
        }

        customer.value = null;
        failed.value = true;
    } finally {
        if (latestId === id) {
            loading.value = false;
        }
    }
};

watch(() => patient.value?.id ?? null, (id) => {
    if (id === null) {
        latestId = null;
        customer.value = null;
        failed.value = false;

        return;
    }

    if (id !== customer.value?.id) {
        void load(id);
    }
}, {immediate: true});

/** The number was just certified: read the file again so the recap can leave. */
const handleCertified = async () => {
    certificationOpen.value = false;

    if (patient.value) {
        await load(patient.value.id);
    }
};
</script>

<template>
  <section class="rounded-lg bg-white p-4 shadow">
    <h2 class="mb-2 text-sm font-semibold text-primary">
      Le patient
    </h2>
    <AgendaPatientSearch v-model="patient" />

    <div v-if="loading" class="mt-2 flex items-center gap-2 text-xs text-gray-500">
      <Loader2 class="size-3.5 animate-spin" />
      Lecture du dossier…
    </div>

    <p v-else-if="failed" class="mt-2 text-xs text-gray-600">
      Le dossier du patient n'a pas pu être lu. Sélectionnez-le à nouveau pour réessayer.
    </p>

    <template v-else-if="customer">
      <div
        v-if="customer.information?.phone"
        class="mt-2 flex items-center gap-1.5 text-xs text-gray-500"
      >
        <Smartphone class="size-3.5" />
        {{ customer.information.phone }} — le récapitulatif du parcours lui sera envoyé par SMS.
      </div>
      <div
        v-else
        class="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-orange/20 px-2.5 py-2 text-xs leading-snug text-gray-dark"
      >
        <span class="flex items-start gap-1.5">
          <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
          Sans mobile, le patient ne recevra pas le récapitulatif de son parcours.
        </span>
        <Dialog v-model:open="certificationOpen">
          <Button type="button" variant="outline" size="sm" @click="certificationOpen = true">
            Renseigner le mobile
          </Button>
          <PreCertificationDialogContent :customer="customer" :on-certified="handleCertified" />
        </Dialog>
      </div>
    </template>
  </section>
</template>
